import {
  throwError as observableThrowError,
  Observable,
  EMPTY,
  timer,
  Subscription,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Map } from 'immutable';
import { config } from 'src/app/environment-config';
import { AppState } from 'src/app/state/app-state';
import { CENTRAL_HOUR, HOST_IP } from 'src/app/state/app.reducer';
import { ConfirmationDialogComponent } from 'src/components/common/confirmationDialog/confirmationDialog.component';
import { CustomDialogComponent } from 'src/components/common/customDialog/customDialog.component';
import { NotificationService } from 'src/core/services/notification.service';
import { ApiGatewayService } from 'src/shared';
import { AppConfigurationApis } from 'src/app/appConfiguration.class';
import { HttpClient } from '@angular/common/http';
import { ExcludeAuthHeader } from '@http/auth-interceptor';
import { UjetDataResponse } from '@models/ujet.class';

const env = config();
const { timeOuts_timeOut1Min } = env;

@Injectable()
export class UtilitiesService {
  private intervalTimeOut: number = +timeOuts_timeOut1Min;
  private loggedInSubscription: Subscription;
  private intervalCentralHourSubscription: Subscription;
  private appConfiguration$: Observable<any>;
  private gbmAPI: string;
  private ujetAuthUrl: string;

  constructor(
    private apiGatewayService: ApiGatewayService,
    private http: HttpClient,
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private dialog: MatDialog,
  ) {
    this.appConfiguration$ = this.store.select('config');

    this.appConfiguration$
      .pipe(
        filter((mapShadowed: any) => Map.isMap(mapShadowed)),
        map((e) => e.toJS()),
        take(1),
      )
      .subscribe((config: AppConfigurationApis) => {
        this.ujetAuthUrl = config.ujetAuthUrl;
        this.gbmAPI = config.api;
      });

    this.init();
  }

  getPublicIP(): Observable<any> {
    return this.apiGatewayService
      .get(`${this.gbmAPI}api/Utilities/GetPublicIP`)
      .pipe(
        tap((result: any) => {
          this.store.dispatch({
            type: HOST_IP,
            payload: result.response,
          });
        }),
        catchError((error: any) => {
          this.notificationService.showServiceError(error);

          return observableThrowError(error);
        }),
      );
  }

  getCentralHour(isTimer?: boolean) {
    this.intervalCentralHourSubscription &&
      this.intervalCentralHourSubscription.unsubscribe();

    const getCentralHour$ = () => {
      return this.apiGatewayService
        .get(`${this.gbmAPI}api/Utilities/GetCentralHour`, isTimer)
        .pipe(
          tap((centralHour: any) => {
            const { response: datetime } = centralHour;
            this.store.dispatch({
              type: CENTRAL_HOUR,
              payload: datetime,
            });
          }),
          catchError((error) => {
            this.notificationService.showServiceError(error);

            const datetime = new Date().toISOString();
            this.store.dispatch({
              type: CENTRAL_HOUR,
              payload: datetime,
            });

            return EMPTY;
          }),
        );
    };

    this.intervalCentralHourSubscription = timer(
      this.intervalTimeOut,
      this.intervalTimeOut,
    )
      .pipe(switchMap(() => getCentralHour$()))
      .subscribe();

    return getCentralHour$();
  }

  getBrowser(): string {
    const userAgent: any = window.navigator.userAgent;
    const browsers: any = {
      chrome: /chrome/i,
      safari: /safari/i,
      firefox: /firefox/i,
      ie: /internet explorer/i,
    };

    for (const key in browsers) {
      if (browsers[key].test(userAgent)) {
        return key;
      }
    }
    return 'unknown';
  }

  /**
   *
   * TODO: This seems deprecated, as it doesn't get called anywhere
   * @deprecated
   */
  showConfirmation(
    title: string,
    content: string,
    noLabel: string,
    yesLabel: string,
  ): Promise<any> {
    const dialogRef: any = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        title,
        content,
        noLabel,
        yesLabel,
      },
    });

    return dialogRef.afterClosed().toPromise();
  }

  showCustomDialog(data: any): Promise<any> {
    const dialogRef: any = this.dialog.open(CustomDialogComponent, {
      disableClose: true,
      data,
    });

    return dialogRef.afterClosed().toPromise();
  }

  getUjetToken(): Observable<UjetDataResponse> {
    return this.http.get<UjetDataResponse>(this.ujetAuthUrl, {
      headers: ExcludeAuthHeader,
    });
  }

  private init(): void {
    this.loggedInSubscription = this.store
      .select('loggedIn')
      .subscribe((value: boolean) => {
        if (value) {
          this.intervalTimeOut = +timeOuts_timeOut1Min;
        } else {
          if (this.intervalCentralHourSubscription) {
            this.intervalCentralHourSubscription.unsubscribe();
          }
        }
      });
  }
}
